import { useState, useEffect } from 'react';
import { leadController } from '../../controllers/leadController';
import Lead from '../../models/Lead';
import {
  Plus, Search, Filter, Download, Upload, Eye, Edit2, Trash2,
  MoreVertical, X, Check, AlertCircle, ChevronUp, ChevronDown
} from 'lucide-react';

const LeadsPage = () => {
  // State management
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterPriority, setFilterPriority] = useState('All');
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    jobTitle: '',
    leadSource: 'Website',
    status: 'New',
    priority: 'Medium',
    estimatedValue: 0,
    notes: ''
  });
  const [formErrors, setFormErrors] = useState([]);
  const [formSubmitting, setFormSubmitting] = useState(false);
  
  // Table state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch leads on mount
  useEffect(() => {
    fetchLeads();
  }, []);

  // Filter and search leads
  useEffect(() => {
    let result = [...leads];

    // Search filter
    if (searchQuery) {
      result = result.filter(lead => {
        const searchLower = searchQuery.toLowerCase();
        return (
          lead.getFullName().toLowerCase().includes(searchLower) ||
          lead.email.toLowerCase().includes(searchLower) ||
          lead.company.toLowerCase().includes(searchLower) ||
          lead.phone.includes(searchQuery)
        );
      });
    }

    // Status filter
    if (filterStatus !== 'All') {
      result = result.filter(lead => lead.status === filterStatus);
    }

    // Priority filter
    if (filterPriority !== 'All') {
      result = result.filter(lead => lead.priority === filterPriority);
    }

    // Sort
    result.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'fullName') {
        aVal = a.getFullName();
        bVal = b.getFullName();
      }
      
      if (typeof aVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });

    setFilteredLeads(result);
    setCurrentPage(1);
  }, [leads, searchQuery, filterStatus, filterPriority, sortField, sortDirection]);

  // Fetch leads from API
  const fetchLeads = async () => {
    setLoading(true);
    const result = await leadController.getAll();
    
    if (result.success) {
      setLeads(result.data);
    } else {
      alert('Error: ' + result.message);
    }
    
    setLoading(false);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setFormErrors([]);
  };

  // Handle add lead
  const handleAdd = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      company: '',
      jobTitle: '',
      leadSource: 'Website',
      status: 'New',
      priority: 'Medium',
      estimatedValue: 0,
      notes: ''
    });
    setFormErrors([]);
    setShowAddModal(true);
  };

  // Handle add submit
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await leadController.create(formData);

    if (result.success) {
      setShowAddModal(false);
      fetchLeads();
      alert('✅ Lead created successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }

    setFormSubmitting(false);
  };

  // Handle edit
  const handleEdit = (lead) => {
    setSelectedLead(lead);
    setFormData({
      firstName: lead.firstName,
      lastName: lead.lastName,
      email: lead.email,
      phone: lead.phone,
      company: lead.company,
      jobTitle: lead.jobTitle,
      leadSource: lead.leadSource,
      status: lead.status,
      priority: lead.priority,
      estimatedValue: lead.estimatedValue,
      notes: lead.notes
    });
    setFormErrors([]);
    setShowEditModal(true);
  };

  // Handle edit submit
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormErrors([]);

    const result = await leadController.update(selectedLead.id, formData);

    if (result.success) {
      setShowEditModal(false);
      fetchLeads();
      alert('✅ Lead updated successfully!');
    } else {
      setFormErrors(result.errors || [result.message]);
    }

    setFormSubmitting(false);
  };

  // Handle view
  const handleView = (lead) => {
    setSelectedLead(lead);
    setShowViewModal(true);
  };

  // Handle delete
  const handleDelete = async (lead) => {
    if (!window.confirm(`Are you sure you want to delete ${lead.getFullName()}?`)) {
      return;
    }

    const result = await leadController.delete(lead.id);

    if (result.success) {
      fetchLeads();
      alert('✅ Lead deleted successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle export
  const handleExport = async () => {
    const result = await leadController.export({ status: filterStatus }, 'csv');
    
    if (result.success) {
      // Create download link
      const blob = new Blob([result.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      alert('✅ Leads exported successfully!');
    } else {
      alert('Error: ' + result.message);
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentLeads = filteredLeads.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredLeads.length / itemsPerPage);

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '16px',
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1e293b',
      margin: 0,
    },
    headerActions: {
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
    },
    button: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      backgroundColor: '#3b82f6',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    secondaryButton: {
      backgroundColor: '#ffffff',
      color: '#1e293b',
      border: '1px solid #e2e8f0',
    },
    filterBar: {
      display: 'flex',
      gap: '12px',
      marginBottom: '20px',
      flexWrap: 'wrap',
    },
    searchBox: {
      flex: 1,
      minWidth: '250px',
      position: 'relative',
    },
    searchInput: {
      width: '100%',
      padding: '10px 10px 10px 40px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
    },
    searchIcon: {
      position: 'absolute',
      left: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    select: {
      padding: '10px 16px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
    },
    card: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden',
    },
    statsBar: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '24px',
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b',
      marginBottom: '8px',
    },
    statValue: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1e293b',
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
    },
    th: {
      backgroundColor: '#f8fafc',
      padding: '16px',
      textAlign: 'left',
      fontSize: '13px',
      fontWeight: '600',
      color: '#475569',
      borderBottom: '1px solid #e2e8f0',
      cursor: 'pointer',
      userSelect: 'none',
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #e2e8f0',
      fontSize: '14px',
      color: '#1e293b',
    },
    badge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '500',
    },
    actionButton: {
      padding: '6px',
      backgroundColor: 'transparent',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px',
      borderTop: '1px solid #e2e8f0',
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      width: '90%',
      maxWidth: '600px',
      maxHeight: '90vh',
      overflow: 'auto',
    },
    modalHeader: {
      padding: '20px 24px',
      borderBottom: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1e293b',
      margin: 0,
    },
    modalBody: {
      padding: '24px',
    },
    formGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
    },
    input: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      transition: 'border-color 0.2s',
    },
    textarea: {
      width: '100%',
      padding: '10px 12px',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '14px',
      outline: 'none',
      resize: 'vertical',
      minHeight: '100px',
    },
    errorList: {
      backgroundColor: '#fee2e2',
      border: '1px solid #fecaca',
      borderRadius: '8px',
      padding: '12px 16px',
      marginBottom: '20px',
    },
    errorItem: {
      color: '#dc2626',
      fontSize: '14px',
      marginBottom: '4px',
    },
    modalFooter: {
      padding: '16px 24px',
      borderTop: '1px solid #e2e8f0',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '12px',
    },
  };

  // Calculate stats
  const stats = {
    total: leads.length,
    new: leads.filter(l => l.status === 'New').length,
    qualified: leads.filter(l => l.status === 'Qualified').length,
    converted: leads.filter(l => l.status === 'Converted').length,
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Leads Management</h1>
        <div style={styles.headerActions}>
          <button
            style={{ ...styles.button, ...styles.secondaryButton }}
            onClick={handleExport}
          >
            <Download size={18} />
            Export
          </button>
          <button style={styles.button} onClick={handleAdd}>
            <Plus size={18} />
            Add Lead
          </button>
        </div>
      </div>

      {/* Stats */}
      <div style={styles.statsBar}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Leads</div>
          <div style={styles.statValue}>{stats.total}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>New</div>
          <div style={{ ...styles.statValue, color: '#3b82f6' }}>{stats.new}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Qualified</div>
          <div style={{ ...styles.statValue, color: '#10b981' }}>{stats.qualified}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Converted</div>
          <div style={{ ...styles.statValue, color: '#8b5cf6' }}>{stats.converted}</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterBar}>
        <div style={styles.searchBox}>
          <Search size={18} color="#94a3b8" style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search leads..."
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          style={styles.select}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="New">New</option>
          <option value="Contacted">Contacted</option>
          <option value="Qualified">Qualified</option>
          <option value="Unqualified">Unqualified</option>
          <option value="Converted">Converted</option>
        </select>
        <select
          style={styles.select}
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
        >
          <option value="All">All Priority</option>
          <option value="High">High</option>
          <option value="Medium">Medium</option>
          <option value="Low">Low</option>
        </select>
      </div>

      {/* Table */}
      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            Loading leads...
          </div>
        ) : currentLeads.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            No leads found. Click "Add Lead" to create one.
          </div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th} onClick={() => handleSort('fullName')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Name
                      {sortField === 'fullName' && (
                        sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </div>
                  </th>
                  <th style={styles.th} onClick={() => handleSort('email')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Email
                      {sortField === 'email' && (
                        sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </div>
                  </th>
                  <th style={styles.th}>Phone</th>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Priority</th>
                  <th style={styles.th} onClick={() => handleSort('estimatedValue')}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Value
                      {sortField === 'estimatedValue' && (
                        sortDirection === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />
                      )}
                    </div>
                  </th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentLeads.map((lead) => (
                  <tr key={lead.id} style={{ cursor: 'pointer' }}>
                    <td style={styles.td} onClick={() => handleView(lead)}>
                      <div style={{ fontWeight: '500' }}>{lead.getFullName()}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>{lead.jobTitle}</div>
                    </td>
                    <td style={styles.td}>{lead.email}</td>
                    <td style={styles.td}>{lead.phone}</td>
                    <td style={styles.td}>{lead.company}</td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: `${lead.getStatusColor()}20`,
                        color: lead.getStatusColor()
                      }}>
                        {lead.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{
                        ...styles.badge,
                        backgroundColor: `${lead.getPriorityColor()}20`,
                        color: lead.getPriorityColor()
                      }}>
                        {lead.priority}
                      </span>
                    </td>
                    <td style={styles.td}>₹{lead.estimatedValue.toLocaleString()}</td>
                    <td style={styles.td}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          style={styles.actionButton}
                          onClick={(e) => { e.stopPropagation(); handleView(lead); }}
                          title="View"
                        >
                          <Eye size={18} color="#64748b" />
                        </button>
                        <button
                          style={styles.actionButton}
                          onClick={(e) => { e.stopPropagation(); handleEdit(lead); }}
                          title="Edit"
                        >
                          <Edit2 size={18} color="#3b82f6" />
                        </button>
                        <button
                          style={styles.actionButton}
                          onClick={(e) => { e.stopPropagation(); handleDelete(lead); }}
                          title="Delete"
                        >
                          <Trash2 size={18} color="#ef4444" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination */}
            <div style={styles.pagination}>
              <div style={{ fontSize: '14px', color: '#64748b' }}>
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredLeads.length)} of {filteredLeads.length} leads
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 12px' }}>
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Add Modal - CONTINUED IN NEXT MESSAGE */}

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div style={styles.modal} onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{showAddModal ? 'Add New Lead' : 'Edit Lead'}</h2>
              <button
                style={{ ...styles.actionButton, padding: '8px' }}
                onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
              >
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={showAddModal ? handleAddSubmit : handleEditSubmit}>
              <div style={styles.modalBody}>
                {formErrors.length > 0 && (
                  <div style={styles.errorList}>
                    {formErrors.map((error, index) => (
                      <div key={index} style={styles.errorItem}>
                        <AlertCircle size={14} style={{ display: 'inline', marginRight: '8px' }} />
                        {error}
                      </div>
                    ))}
                  </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      style={styles.input}
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      style={styles.input}
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Email *</label>
                    <input
                      type="email"
                      name="email"
                      style={styles.input}
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      style={styles.input}
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Company</label>
                    <input
                      type="text"
                      name="company"
                      style={styles.input}
                      value={formData.company}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Job Title</label>
                    <input
                      type="text"
                      name="jobTitle"
                      style={styles.input}
                      value={formData.jobTitle}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Lead Source</label>
                    <select
                      name="leadSource"
                      style={styles.input}
                      value={formData.leadSource}
                      onChange={handleInputChange}
                    >
                      <option value="Website">Website</option>
                      <option value="Referral">Referral</option>
                      <option value="Social Media">Social Media</option>
                      <option value="Cold Call">Cold Call</option>
                      <option value="Advertisement">Advertisement</option>
                      <option value="Trade Show">Trade Show</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Status</label>
                    <select
                      name="status"
                      style={styles.input}
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="New">New</option>
                      <option value="Contacted">Contacted</option>
                      <option value="Qualified">Qualified</option>
                      <option value="Unqualified">Unqualified</option>
                      <option value="Converted">Converted</option>
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Priority</label>
                    <select
                      name="priority"
                      style={styles.input}
                      value={formData.priority}
                      onChange={handleInputChange}
                    >
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.label}>Estimated Value (₹)</label>
                    <input
                      type="number"
                      name="estimatedValue"
                      style={styles.input}
                      value={formData.estimatedValue}
                      onChange={handleInputChange}
                      min="0"
                    />
                  </div>
                </div>

                <div style={styles.formGroup}>
                  <label style={styles.label}>Notes</label>
                  <textarea
                    name="notes"
                    style={styles.textarea}
                    value={formData.notes}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div style={styles.modalFooter}>
                <button
                  type="button"
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={() => { setShowAddModal(false); setShowEditModal(false); }}
                  disabled={formSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.button}
                  disabled={formSubmitting}
                >
                  {formSubmitting ? 'Saving...' : (showAddModal ? 'Create Lead' : 'Update Lead')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedLead && (
        <div style={styles.modal} onClick={() => setShowViewModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Lead Details</h2>
              <button
                style={{ ...styles.actionButton, padding: '8px' }}
                onClick={() => setShowViewModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Full Name</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedLead.getFullName()}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Email</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedLead.email}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Phone</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedLead.phone}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Company</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedLead.company || 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Job Title</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedLead.jobTitle || 'N/A'}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Lead Source</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {selectedLead.leadSource}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</div>
                  <span style={{
                    ...styles.badge,
                    backgroundColor: `${selectedLead.getStatusColor()}20`,
                    color: selectedLead.getStatusColor()
                  }}>
                    {selectedLead.status}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Priority</div>
                  <span style={{
                    ...styles.badge,
                    backgroundColor: `${selectedLead.getPriorityColor()}20`,
                    color: selectedLead.getPriorityColor()
                  }}>
                    {selectedLead.priority}
                  </span>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Estimated Value</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    ₹{selectedLead.estimatedValue.toLocaleString()}
                  </div>
                </div>

                <div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Created</div>
                  <div style={{ fontSize: '16px', fontWeight: '500', color: '#1e293b' }}>
                    {new Date(selectedLead.createdAt).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {selectedLead.notes && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Notes</div>
                  <div style={{ 
                    fontSize: '14px', 
                    color: '#1e293b',
                    padding: '12px',
                    backgroundColor: '#f8fafc',
                    borderRadius: '8px'
                  }}>
                    {selectedLead.notes}
                  </div>
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                style={{ ...styles.button, ...styles.secondaryButton }}
                onClick={() => {
                  setShowViewModal(false);
                  handleEdit(selectedLead);
                }}
              >
                <Edit2 size={16} />
                Edit Lead
              </button>
              <button
                style={styles.button}
                onClick={() => setShowViewModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsPage;